// Real game clock (design doc 16.34/16.36, revised): "the entire game
// runs on a single continuous real-time clock... logging out to the main
// menu, or closing the tab/browser, pauses time; otherwise everything
// keeps running, even across other UI screens or a minimized tab."
// Extended per Don's direction: the clock ALSO pauses while docked (Base
// View or Station View), not just on logout -- it only runs while the
// ship is actually out in the Expedition View (including any overlay UI
// opened on top of it, which never leaves the Expedition View, 16.35/
// 16.36).
//
// Implemented as a "pausable stopwatch": rather than tracking a separate
// simulated clock, `effectiveNow()` is real wall-clock time minus the
// total time spent paused so far -- a monotonically increasing value that
// simply skips over paused spans. Every duration-based check (VIP
// deadlines today, Provisions consumption later) should compare against
// `effectiveNow()`, never raw Date.now()/`performance.now()`, per 16.30's
// load-bearing "absolute timestamp, never a decrementing counter" rule --
// this is that same rule, extended to survive real pause spans too.
export interface ClockState {
  pausedAccumMs: number;
  // Epoch ms the current pause began, or null if not currently paused.
  pauseStartedAt: number | null;
}

export function initialClockState(nowMs: number): ClockState {
  // Starts paused -- the app boots into the Main Menu/login, itself a
  // paused context (see isClockPaused), so there's nothing to "resume"
  // until real gameplay begins.
  return { pausedAccumMs: 0, pauseStartedAt: nowMs };
}

// The game-time equivalent of Date.now() -- frozen at the moment a pause
// began if currently paused, so nothing observes time "passing" while
// paused even before resumeClock() formally ends it.
export function effectiveNow(clock: ClockState, nowMs: number): number {
  if (clock.pauseStartedAt !== null) {
    return clock.pauseStartedAt - clock.pausedAccumMs;
  }
  return nowMs - clock.pausedAccumMs;
}

// Both no-ops if already in the requested state -- safe to call
// unconditionally on every render (see app.ts), rather than needing
// separate edge-triggered enter/exit hooks per pause reason.
export function pauseClock(clock: ClockState, nowMs: number): ClockState {
  if (clock.pauseStartedAt !== null) {
    return clock;
  }
  return { ...clock, pauseStartedAt: nowMs };
}

export function resumeClock(clock: ClockState, nowMs: number): ClockState {
  if (clock.pauseStartedAt === null) {
    return clock;
  }
  return { pausedAccumMs: clock.pausedAccumMs + (nowMs - clock.pauseStartedAt), pauseStartedAt: null };
}
